// packages/web/src/hooks/useBuyBoxLogic.ts
// Custom hook for BuyBox business logic - extracted from BuyBox component
import React from 'react';

import { compareBuyBoxPerks, getAlternativePerks } from '../utils/buyBoxWinner';
import { filterPerksForQuote, PerkQuoteContext } from '../utils/perkRules';

interface ServiceData {
  id: string;
  name: string;
  description?: string;
  category?: string;
  base_price?: number | string;
  price_type?: string;
  commission_rate?: number;
  service_type?: string;
  metadata?: any;
  average_rating?: number;
  review_count?: number;
  owner_tenant_id?: string;
  owner_company?: string;
  brand_logo_path?: string;
  company_description?: string;
  perks?: any[];
  winningPerk?: any;
  allPerks?: any[];
}

interface UseBuyBoxLogicProps {
  service: ServiceData;
  quoteContext?: PerkQuoteContext;
}

interface UseBuyBoxLogicReturn {
  // Data
  allPerks: any[];
  winningPerk: any;
  alternativePerks: any[];
  buyOptions: any[];
  sortedOptions: any[];
  visibleOptions: any[];

  // State
  selectedOption: string;
  showAllOptions: boolean;

  // Actions
  setSelectedOption: (option: string) => void;
  setShowAllOptions: (show: boolean) => void;

  // Helpers
  getPartnerDisplayName: (option: any) => string;
}

export const useBuyBoxLogic = ({ service, quoteContext }: UseBuyBoxLogicProps): UseBuyBoxLogicReturn => {
  console.log('[useBuyBoxLogic][INIT] ===== BUYBOX LOGIC HOOK INITIALIZED =====');
  console.log('[useBuyBoxLogic][INIT] Service ID:', service?.id);
  console.log('[useBuyBoxLogic][INIT] Service name:', service?.name);
  console.log('[useBuyBoxLogic][INIT] Service has allPerks:', Array.isArray(service?.allPerks));
  console.log('[useBuyBoxLogic][INIT] Service allPerks length:', service?.allPerks?.length || 0);
  console.log('[useBuyBoxLogic][INIT] Service has perks:', Array.isArray(service?.perks));
  console.log('[useBuyBoxLogic][INIT] Service perks length:', service?.perks?.length || 0);

  // State management
  const [selectedOption, setSelectedOption] = React.useState<string>('0');
  const [showAllOptions, setShowAllOptions] = React.useState<boolean>(false);

  // Get perks data - use server-provided data to prevent hydration issues
  const allPerks = React.useMemo(() => {
    const perks = service.allPerks || service.perks || [];
    const quoteFilteredPerks = quoteContext ? filterPerksForQuote(perks, quoteContext) : perks;
    console.log(
      '[useBuyBoxLogic][PERKS] Using server-provided allPerks:',
      perks.length,
      'perks found; quote-eligible:',
      quoteFilteredPerks.length,
    );
    return quoteFilteredPerks.length > 0 ? quoteFilteredPerks : [];
  }, [service.allPerks, service.perks, quoteContext]);

  // Use server-provided winningPerk instead of recalculating client-side
  const winningPerk = React.useMemo(() => {
    console.log(
      '[useBuyBoxLogic][WINNER] Using server-provided winningPerk:',
      service.winningPerk?.name || 'none',
    );
    if (allPerks.length === 0) return null;
    if (service.winningPerk && allPerks.some((perk) => perk.id === service.winningPerk.id)) {
      return allPerks.find((perk) => perk.id === service.winningPerk.id) || service.winningPerk;
    }
    return [...allPerks].sort(compareBuyBoxPerks)[0] || null;
  }, [service.winningPerk, allPerks]);

  // Calculate alternative perks
  const alternativePerks = React.useMemo(() => {
    if (allPerks.length === 0 || !winningPerk) return [];
    return getAlternativePerks(allPerks, winningPerk);
  }, [allPerks, winningPerk]);

  // Helper to get partner display name - prioritize perk partner over service supplier
  const getPartnerDisplayName = React.useCallback(
    (option: any) => {
      // First priority: perk's partner (the one offering the perk)
      const perkPartner = option.partner_company || option.partner_name;
      if (perkPartner) {
        return perkPartner;
      }

      // Fallback: service supplier (owner company) — never expose internal platform name
      const serviceSupplier = service?.owner_company || '';
      return serviceSupplier;
    },
    [service?.owner_company],
  );

  // Debug: Log perk data to see what's available
  React.useEffect(() => {
    console.log('[useBuyBoxLogic] Server-provided perks data:', allPerks.length, 'perks found');
    console.log('[useBuyBoxLogic] Server-provided winning perk:', winningPerk?.name || 'none');
    console.log('[useBuyBoxLogic] Alternative perks:', alternativePerks.length);
  }, [allPerks.length, winningPerk?.name, alternativePerks.length]);

  // DEBUG: Log buy options creation
  console.log('[useBuyBoxLogic] Creating buy options:', {
    allPerksCount: allPerks.length,
    hasWinningPerk: !!winningPerk,
    winningPerkName: winningPerk?.name || 'none',
  });

  // Logic: If perks available, never show base service option
  // Always show perk combinations, or base service if no perks
  const buyOptions =
    allPerks.length > 0 ? allPerks : [{ id: 'base', name: 'Base Service', perk_type: null }];

  console.log(
    '[useBuyBoxLogic] Final buy options:',
    buyOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      type: opt.perk_type,
    })),
  );

  // Sort options with the same neutral policy used by shared winner selection.
  // Prefer server scoring when available, otherwise apply a small service-first
  // preference without auto-prioritizing rebate or gift-card perks.
  const sortedOptions = React.useMemo(() => {
    if (buyOptions.length <= 1) return buyOptions;

    return [...buyOptions].sort(compareBuyBoxPerks);
  }, [buyOptions]);

  // Show only top 3 initially, or all if expanded
  const visibleOptions = showAllOptions ? sortedOptions : sortedOptions.slice(0, 3);

  return {
    // Data
    allPerks,
    winningPerk,
    alternativePerks,
    buyOptions,
    sortedOptions,
    visibleOptions,

    // State
    selectedOption,
    showAllOptions,

    // Actions
    setSelectedOption,
    setShowAllOptions,

    // Helpers
    getPartnerDisplayName,
  };
};
